import {
  Applicant,
  EMPTY_IMMIGRATION_PROBLEM,
  EMPTY_PREVIOUS_LEAVE_APPLICATION,
  ImmigrationProblem,
} from "../../types/applicants";
import { EMPTY_COUNTRY, Country } from "../../types/commons";

const buildMonthDate = (existingValue = "", datePart: "month" | "year", nextValue: string) => {
  const [currentYear = "", currentMonth = ""] = existingValue.split("-");
  const year = datePart === "year" ? nextValue : currentYear;
  const month = datePart === "month" ? nextValue : currentMonth;

  if (!year && !month) {
    return "";
  }

  if (!year) {
    return month ? `-${month}` : "";
  }

  if (!month) {
    return `${year}-`;
  }

  return `${year}-${month}`;
};

export const handlePreviousLeaveChange = (
  applicant: Applicant,
  event: React.ChangeEvent<HTMLInputElement>
) => {
  const key = event.target.name;

  if (!applicant || !key.startsWith("prev_leave_")) {
    return null;
  }

  const [, , indexString, ...fieldParts] = key.split("_");
  const index = parseInt(indexString, 10);
  const field = fieldParts.join("_");

  if (Number.isNaN(index) || !field) {
    return null;
  }

  const previousLeaveApplications = applicant.previous_leave_applications
    ? [...applicant.previous_leave_applications]
    : [];

  while (previousLeaveApplications.length <= index) {
    previousLeaveApplications.push({ ...EMPTY_PREVIOUS_LEAVE_APPLICATION });
  }

  const updatedApplication = {
    ...previousLeaveApplications[index],
  };

  if (field === "date_month") {
    updatedApplication.date = buildMonthDate(updatedApplication.date, "month", event.target.value);
  } else if (field === "date_year") {
    updatedApplication.date = buildMonthDate(updatedApplication.date, "year", event.target.value);
  } else if (field === "result") {
    updatedApplication.result = event.target.value as "approved" | "refused" | "";
  } else if (field === "refusal_reason") {
    updatedApplication.refusal_reason = event.target.value;
  } else {
    return null;
  }

  if (field === "result" && event.target.value !== "refused") {
    updatedApplication.refusal_reason = "";
  }

  previousLeaveApplications[index] = updatedApplication;

  return {
    ...applicant,
    previous_leave_applications: previousLeaveApplications,
  };
};

export const handleAddPreviousLeave = (applicant: Applicant) => {
  if (!applicant) {
    return null;
  }

  return {
    ...applicant,
    previous_leave_applications: [
      ...(applicant.previous_leave_applications || []),
      { ...EMPTY_PREVIOUS_LEAVE_APPLICATION },
    ],
  };
};

export const handleDeletePreviousLeave = (applicant: Applicant, index: number) => {
  if (!applicant) {
    return null;
  }

  const previousLeaveApplications = [...(applicant.previous_leave_applications || [])];
  previousLeaveApplications.splice(index, 1);

  return {
    ...applicant,
    previous_leave_applications: previousLeaveApplications,
  };
};

export const handleImmigrationProblemChange = (
  applicant: Applicant,
  event: React.ChangeEvent<HTMLInputElement>
) => {
  const key = event.target.name;

  if (!applicant || !key.startsWith("imm_problem_")) {
    return null;
  }

  const [, , indexString, ...fieldParts] = key.split("_");
  const index = parseInt(indexString, 10);
  const field = fieldParts.join("_");

  if (Number.isNaN(index) || !field) {
    return null;
  }

  const immigrationProblems = applicant.immigration_problems
    ? [...applicant.immigration_problems]
    : [];

  while (immigrationProblems.length <= index) {
    immigrationProblems.push({ ...EMPTY_IMMIGRATION_PROBLEM });
  }

  const value = field === "details" ? event.target.value.slice(0, 500) : event.target.value;

  immigrationProblems[index] = {
    ...immigrationProblems[index],
  };

  if (field === "date_month") {
    immigrationProblems[index].date = buildMonthDate(immigrationProblems[index].date, "month", value);
  } else if (field === "date_year") {
    immigrationProblems[index].date = buildMonthDate(immigrationProblems[index].date, "year", value);
  } else if (field === "type") {
    immigrationProblems[index].type = value as ImmigrationProblem["type"];
  } else if (field === "uk_appeal_awaiting") {
    const raw = String(event.target.value);
    immigrationProblems[index].uk_appeal_awaiting = raw === "true";
  } else if (field === "details") {
    immigrationProblems[index].details = value;
  } else {
    return null;
  }

  return {
    ...applicant,
    immigration_problems: immigrationProblems,
  };
};

export const handleImmigrationProblemCountryChange = (
  applicant: Applicant,
  event: any,
  newValue: Country | null
) => {
  if (!applicant) {
    return null;
  }

  const key = event.target.id.split("-option")[0];

  if (!key.startsWith("imm_problem_country_")) {
    return null;
  }

  const index = parseInt(key.split("_")[3], 10);

  if (Number.isNaN(index)) {
    return null;
  }

  const immigrationProblems = applicant.immigration_problems ? [...applicant.immigration_problems] : [];

  while (immigrationProblems.length <= index) {
    immigrationProblems.push({ ...EMPTY_IMMIGRATION_PROBLEM });
  }

  const country = newValue ?? { ...EMPTY_COUNTRY };
  const previous = immigrationProblems[index];
  immigrationProblems[index] = {
    ...previous,
    country,
  };
  if (country.code !== "GB") {
    delete immigrationProblems[index].uk_appeal_awaiting;
  }

  return {
    ...applicant,
    immigration_problems: immigrationProblems,
  };
};

export const handleAddImmigrationProblem = (applicant: Applicant) => {
  if (!applicant) {
    return null;
  }

  return {
    ...applicant,
    immigration_problems: [
      ...(applicant.immigration_problems || []),
      { ...EMPTY_IMMIGRATION_PROBLEM },
    ],
  };
};

export const handleDeleteImmigrationProblem = (applicant: Applicant, index: number) => {
  if (!applicant) {
    return null;
  }

  const immigrationProblems = [...(applicant.immigration_problems || [])];
  immigrationProblems.splice(index, 1);

  return {
    ...applicant,
    immigration_problems: immigrationProblems,
  };
};
